import { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { observer } from "mobx-react-lite";
import { supabase } from "@/integrations/supabase/client";
import { PageLayout } from "@/components/layout/PageLayout";
import { usePermissions } from "@/hooks/usePermissions";
import { useRealtimeInvalidate } from "@/hooks/realtime/useRealtimeInvalidate";
import { realtimeTopic } from "@/lib/realtime/channel";
import { Badge } from "@/components/ui/badge";
import { Loader2, FileText } from "lucide-react";
import { PageTitle } from "@/components/ui/page-title";
import { PageSubtitle } from "@/components/ui/page-subtitle";
import { useLanguage } from "@/lib/i18n/LanguageContext";

/**
 * Per-project documents log at /projects/:id/log.
 *
 * RLS already filters `documents` rows to what the caller can see; this page adds
 * a project_id equality filter and orders recent-first. Covers the xlsx row
 * "View document log: Yes, filtered by the project" for subcontractors, and is
 * also a useful project-scoped view for owners.
 */
export const ProjectDocumentsLog = observer(function ProjectDocumentsLog() {
  const { id } = useParams<{ id: string }>();
  const projectIdNum = Number(id);
  const { canViewLogOnProject } = usePermissions();
  const { t } = useLanguage();

  const { data, isLoading, error } = useQuery({
    queryKey: ["project-documents-log", projectIdNum],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("documents")
        .select(
          "id, building_id, filename, content_type, version, is_deleted, created_by, created_at, modified_by, modified_at, state"
        )
        .eq("project_id", projectIdNum)
        .eq("hidden", false)
        .order("modified_at", { ascending: false })
        .limit(200);
      if (error) throw error;
      return data;
    },
    enabled: !!projectIdNum,
    // The realtime bridge below is the freshness mechanism; this only
    // bounds how stale a remount can start out.
    staleTime: 30_000,
  });

  // The building this project belongs to, read off the rows themselves rather
  // than looked up separately: every document in a project belongs to that
  // project's building, and these rows are already through RLS. A second query
  // to `building_projects` would add a failure mode of its own — one this page
  // could only degrade silently around, since the subscription is all that
  // keeps it live.
  //
  // Latched, never given back: a project emptied of documents (every one moved
  // out) would otherwise drop the channel back to `project_id=eq.N`, which is
  // exactly the move-out blind spot the building scope exists to close — and
  // pay a second channel swap to do it. A building does not change under a
  // project, so the first row to name one names it for good.
  const [buildingId, setBuildingId] = useState<number | null>(null);
  const rowBuildingId = data?.[0]?.building_id ?? null;
  useEffect(() => {
    if (rowBuildingId !== null) setBuildingId(rowBuildingId);
  }, [rowBuildingId]);

  // This route sits outside BuildingLayout, so the building-scoped bridge
  // mounted there does not reach it — and the rows carry each document's
  // pipeline state verbatim, which made a frozen list read as a stalled
  // pipeline rather than a stale page.
  // Gated on the same permission as the redirect below, not on the redirect
  // itself: hooks run before it, so a user without the log permission would
  // otherwise open a channel on this project for the render that bounces them.
  //
  // Scoped to the BUILDING, not to this project: walrus evaluates a filter
  // against the new record on INSERT/UPDATE and only falls back to the old one
  // on DELETE, so `project_id=eq.N` never hears about a document moved OUT of
  // this project — the list would keep showing a row that has left it. The
  // building filter still sees that update, and the query re-reads by project
  // anyway, so no client-side matching is needed.
  //
  // Before the first row lands there is no building to scope to, so the project
  // filter stands in. Its blind spot needs a row to have left this project, and
  // an empty list has none; the INSERT that fills the list does match it, and
  // the resulting rows re-scope the channel to the building.
  //
  // That re-scope is a channel teardown and a fresh join, and events landing in
  // between are gone — postgres_changes has no replay, and the replacement's
  // first SUBSCRIBED would read as an initial join with no onResync behind it.
  // `flushOnJoin` makes every join, including that one, refetch the list.
  useRealtimeInvalidate({
    enabled: Boolean(id) && Boolean(projectIdNum) && canViewLogOnProject(projectIdNum),
    topic: realtimeTopic("project-documents-log", projectIdNum),
    bindings: [
      {
        event: "*",
        table: "documents",
        filter: buildingId ? `building_id=eq.${buildingId}` : `project_id=eq.${projectIdNum}`,
      },
    ],
    invalidate: [{ queryKey: ["project-documents-log", projectIdNum] }],
    flushOnJoin: true,
    windowMs: 1_000,
  });

  if (!id) return <Navigate to="/buildings" replace />;
  if (!canViewLogOnProject(projectIdNum)) return <Navigate to="/buildings" replace />;

  return (
    <PageLayout showNavigationButtons customPadding>
      <div className="mt-20 flex w-full flex-col gap-4 border-t p-6">
        <PageTitle info="projectLog" infoProjectId={projectIdNum}>
          {t.pageInfo.projectLogTitle}
        </PageTitle>
        <PageSubtitle>
          Activity for documents in this project. Soft-deleted items are shown so you can see
          everything that's happened here.
        </PageSubtitle>

        {isLoading && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}

        {error && <div className="text-destructive">Failed to load log.</div>}

        {data && data.length === 0 && (
          <div className="text-muted-foreground">No documents in this project yet.</div>
        )}

        {data && data.length > 0 && (
          <ul className="divide-y rounded-md border">
            {data.map((d) => (
              <li key={d.id} className="flex items-center gap-3 p-3">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <div className="flex-1">
                  <div className="font-medium">
                    {d.filename}
                    {d.is_deleted && (
                      <Badge variant="secondary" className="ml-2">
                        archived
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    v{d.version} · {d.state} · modified{" "}
                    {d.modified_at ? new Date(d.modified_at).toLocaleString() : "—"} · by #
                    {d.modified_by ?? d.created_by}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </PageLayout>
  );
});
